import React, { useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import UtilityBillModal from '../components/UtilityBillModal';
import UtilityAnalytics from '../components/UtilityAnalytics';

const { FiZap, FiPlus, FiDroplet, FiWind, FiWifi, FiSearch, FiFilter, FiTrendingUp, FiAlertCircle, FiTrash2, FiActivity, FiArrowRight } = FiIcons;

const UtilitiesTracker = () => {
  const { data, addRecord, deleteRecord } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeType, setActiveType] = useState('Electricity');
  const [filterProp, setFilterProp] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const bills = data.utilityBills || [];

  const filteredBills = useMemo(() => {
    return bills.filter(b => {
      const matchesProp = filterProp === 'all' || b.propertyId === filterProp;
      const matchesSearch = b.type.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesProp && matchesSearch;
    }).sort((a, b) => new Date(b.billingMonth) - new Date(a.billingMonth));
  }, [bills, filterProp, searchTerm]);

  const stats = {
    totalSpend: filteredBills.reduce((sum, b) => sum + Number(b.amount), 0),
    avgBill: filteredBills.length > 0 ? (filteredBills.reduce((sum, b) => sum + Number(b.amount), 0) / filteredBills.length).toFixed(2) : 0,
    highestBill: Math.max(...filteredBills.map(b => Number(b.amount)), 0)
  };

  const getIcon = (type) => {
    switch (type) {
      case 'Electricity': return FiZap;
      case 'Water': return FiDroplet;
      case 'Gas': return FiWind;
      case 'Internet': return FiWifi;
      default: return FiActivity;
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Utility Intelligence</h2>
          <p className="text-sm text-gray-500 font-medium italic">Monitoring consumption patterns and overhead efficiency.</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider">
          <SafeIcon icon={FiPlus} className="mr-2" /> Log Monthly Bill
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden group">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Utility Spend</p>
              <p className="text-3xl font-black text-gray-900">${stats.totalSpend.toLocaleString()}</p>
              <SafeIcon icon={FiTrendingUp} className="absolute -bottom-2 -right-2 text-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity" size={60} />
            </div>
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Avg. Monthly Bill</p>
              <p className="text-3xl font-black text-indigo-600">${stats.avgBill}</p>
            </div>
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden group">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Peak Expenditure</p>
              <p className="text-3xl font-black text-red-600">${stats.highestBill.toLocaleString()}</p>
              <SafeIcon icon={FiAlertCircle} className="absolute -bottom-2 -right-2 text-red-50 opacity-0 group-hover:opacity-100 transition-opacity" size={60} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-3xl border border-gray-100 shadow-sm mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex bg-gray-50 p-1 rounded-2xl overflow-x-auto w-full md:w-auto">
              {['Electricity', 'Water', 'Gas', 'Internet'].map(t => (
                <button
                  key={t}
                  onClick={() => setActiveType(t)}
                  className={`flex items-center px-6 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all whitespace-nowrap ${activeType === t ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <SafeIcon icon={getIcon(t)} className="mr-2" /> {t}
                </button>
              ))}
            </div>
            <div className="relative flex-1 max-w-xs w-full">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search bills..." className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border-none rounded-xl outline-none text-xs font-bold" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>

          <UtilityAnalytics bills={bills} type={activeType} />
        </div>

        <div className="space-y-6">
          <div className="bg-indigo-600 p-6 rounded-3xl text-white shadow-xl relative overflow-hidden group">
            <h3 className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-2">Efficiency Score</h3>
            <p className="text-5xl font-black">A+</p>
            <p className="text-[10px] font-bold uppercase mt-2 text-indigo-100">Portfolio Sustainability</p>
            <SafeIcon icon={FiZap} className="absolute -bottom-4 -right-4 text-white/10 group-hover:scale-110 transition-transform" size={100} />
          </div>

          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Property Filter</h3>
            <select className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-black outline-none focus:ring-2 focus:ring-indigo-500" value={filterProp} onChange={e => setFilterProp(e.target.value)}>
              <option value="all">All Properties</option>
              {data.properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
          <h3 className="text-xs font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
            <SafeIcon icon={FiActivity} className="text-indigo-600" /> Bill Ledger
          </h3>
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Showing {filteredBills.length} Entries</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-50">
            <thead className="bg-gray-50/20">
              <tr>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Billing Cycle</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Property & Type</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Usage Metric</th>
                <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount</th>
                <th className="px-6 py-4 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredBills.map(b => {
                const prop = data.properties.find(p => p.id === b.propertyId);
                return (
                  <tr key={b.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-6">
                      <p className="text-sm font-black text-gray-900">{format(parseISO(b.billingMonth + '-01'), 'MMMM yyyy')}</p>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Status: {b.status}</p>
                    </td>
                    <td className="px-6 py-6">
                      <p className="text-sm font-black text-gray-900">{prop?.name || 'Shared'}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <SafeIcon icon={getIcon(b.type)} className="text-indigo-400" size={12} />
                        <p className="text-[10px] text-gray-500 font-bold uppercase">{b.type}</p>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-black text-gray-900">{b.usageValue}</p>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">{b.usageUnit}</span>
                      </div>
                    </td>
                    <td className="px-6 py-6 text-right">
                      <p className="text-sm font-black text-gray-900">${Number(b.amount).toLocaleString()}</p>
                    </td>
                    <td className="px-6 py-6 text-right">
                      <button onClick={() => deleteRecord('utilityBills', b.id)} className="p-2 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredBills.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-24 text-center">
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest">No utility records found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <UtilityBillModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={(b) => addRecord('utilityBills', b)} properties={data.properties} />
    </div>
  );
};

export default UtilitiesTracker;